/**
 * CatalogEntryView — /catalog-entry/bulletin  📋 카탈로그 관리 › 불레틴 (next 메뉴)
 *
 * 머리 · 탭은 메뉴(menu.ts)에서 읽는다 — 옛 구역 탭(③ 불레틴 · ① 등록 요청 · ② 카탈로그 수정)은 2026-10-20 뺌(① 은 카탈로그 요청함으로 넘어가 혼동).
 * 구역 ① requests · ② edit 의 코드는 안 쓰이는 채 남아 있다 — 다음 정리 때 뺀다.
 * 근거: hcltech-parts docs/CATALOG_ENTRY_SCREENS.md.
 *
 * 분해: catalog-entry/types.ts(상태) · catalog-entry/render.ts(그리기, 순수) · 이 파일(핸들러 + 로드 + 부분 재렌더).
 * 구역 전환은 링크(data-link)가 라우터로 보낸다 — 핸들러 없음.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { assert } from '../lib/assert';
import type { CatalogEntryData, CatalogEntrySection } from './catalog-entry/types';
import { renderBulletinDoc, renderBulletinList, renderRequests } from './catalog-entry/render';
import { hasTextSelection } from '../lib/selection';

/** 목록 한 번에 받는 편 수. 탭 하나가 이보다 길면 오래된 것이 잘린다(updated_at 최신순) */
const LIST_LIMIT = 200;
const MENU_HREF = '/catalog-entry/bulletin';

function isViewFilter(raw: string | undefined): raw is api.EBulletinViewFilter {
  return api.E_BULLETIN_VIEW_FILTERS.some((f) => f === raw);
}

/** 같은 편인가 — 열쇠 (vendor, doc_no). 비교 상대가 null 이면 false */
function isSameDoc(doc: api.BulletinDocView, otherOrNull: api.BulletinDocView | null): boolean {
  return otherOrNull !== null && doc.vendor === otherOrNull.vendor && doc.doc_no === otherOrNull.doc_no;
}

export class CatalogEntryView extends SolaView<CatalogEntryData> {
  override events = {
    click: {
      '@bulletin-filter':      'handleBulletinFilter',     // 탭
      '@bulletin-pick':        'handleBulletinPick',       // 목록 줄 → 오른쪽
      '@bulletin-prev':        'handleBulletinPrev',       // 이전 묶음
      '@bulletin-next':        'handleBulletinNext',       // 다음 묶음
      '@bulletin-recheck':     'handleBulletinRecheck',    // 다시 검사
    },
  };

  constructor(args: { section: CatalogEntrySection }) {
    super();
    this.data = {
      section:  args.section,
      bulletin: { filter: 'attention', rows: [], total: 0, offset: 0, limit: LIST_LIMIT, selected: null, loading: true, error: '' },
      requests: { rows: [], loading: true, error: '' },
    };
  }

  override getTitle(): string {
    return '카탈로그 관리 › 불레틴 — HCLTech';
  }

  /** section 에 맞는 구역을 load */
  override onMount(): void {
    if (this.data.section === 'bulletin') void this.loadBulletin();
    if (this.data.section === 'requests') void this.loadRequests();
  }

  render(): HTMLElement {
    const place = findMenuPlace(MENU_HREF);
    assert(place !== null, `불레틴 탭이 메뉴에 없다: ${MENU_HREF} — MENU_GROUPS 제품 › 카탈로그 관리의 탭에 있어야 한다`);
    return htmlSolaWithInstance(this)`
      <div class="page page-catalog-entry pv-page">
        <header class="page-header">
          <h1>${menuHeading(place)}</h1>
          ${renderMenuTabs(place)}
        </header>
        <section class="page-body">
          <div class="ce-body">${this.renderBody()}</div>
        </section>
      </div>
    `;
  }

  // ===== 핸들러 — 던지면 Sola 의 onActionError(alert) 가 받는다. try/catch 는 실패 뒤 할 일이 있을 때만 =====

  handleBulletinFilter(_e: Event, el: HTMLElement): void {
    const filter = el.dataset.filter;
    assert(isViewFilter(filter), `탭 버튼의 data-filter 는 E_BULLETIN_VIEW_FILTERS 중 하나여야 한다 — 탭은 그 배열로 그렸다. 받은 값: ${filter}`);

    this.data.bulletin = { ...this.data.bulletin, filter, offset: 0, loading: true, error: '' };   // 탭이 바뀌면 첫 묶음부터
    this.rerenderBody();
    void this.loadBulletin();
  }

  handleBulletinPick(_e: Event, el: HTMLElement): void {
    if (hasTextSelection()) return;   // 긁어 복사(드래그)면 안 연다 — 클릭과 가른다(2026-10-30 전수)
    const vendor = el.dataset.vendor;
    const docNo  = Number(el.dataset.docNo);
    const picked = this.data.bulletin.rows.find((doc) => doc.vendor === vendor && doc.doc_no === docNo);
    assert(picked !== undefined, `목록 줄이 가리키는 편은 rows 안에 있어야 한다 — 줄은 rows 로 그렸다. ${vendor}/${docNo}`);

    this.data.bulletin.selected = picked;
    this.rerenderBody();
  }

  async handleBulletinRecheck(): Promise<void> {
    const key = this.selectedKey();
    const rechecked = await api.recheckBulletinDoc(key);
    this.replaceSelected(rechecked);
  }

  /** 이전 묶음 — offset − limit(0 아래로는 안 감). offset 이 0 이면 버튼이 disabled 라 안 온다 */
  async handleBulletinPrev(): Promise<void> {
    const offset = Math.max(0, this.data.bulletin.offset - this.data.bulletin.limit);
    await this.moveBulletinOffset(offset);
  }

  /** 다음 묶음 — offset + limit. offset + limit ≥ total 이면 버튼이 disabled 라 안 온다 */
  async handleBulletinNext(): Promise<void> {
    const offset = this.data.bulletin.offset + this.data.bulletin.limit;
    await this.moveBulletinOffset(offset);
  }

  // ===== 로드 · 재렌더 =====

  /** 묶음 자리를 옮기고 다시 불러온다. 탭이 바뀌면 handleBulletinFilter 가 offset 0 으로 */
  private async moveBulletinOffset(offset: number): Promise<void> {
    this.data.bulletin = { ...this.data.bulletin, offset, loading: true, error: '' };
    this.rerenderBody();
    await this.loadBulletin();
  }

  /** filter · offset 으로 한 묶음. total · offset · limit 을 받아 둔다. selected 가 새 묶음에 없으면 null */
  private loadSeq = 0;   // 불러오기 순번 — 늦게 온 옛 응답이 새 결과를 덮지 않게(QA 2026-10-20 #3 응답 경합)

  private async loadBulletin(): Promise<void> {
    const before = this.data.bulletin;
    const seq = ++this.loadSeq;
    try {
      const list = await api.fetchBulletinDocs(before.filter, LIST_LIMIT, before.offset);
      if (seq !== this.loadSeq) return;   // 그 사이 새 불러오기가 시작됐다 — 이 응답은 버린다
      const stillSelected = list.rows.find((doc) => isSameDoc(doc, before.selected));
      this.data.bulletin = {
        ...before,
        rows: list.rows, total: list.total, offset: list.offset, limit: list.limit,
        selected: stillSelected === undefined ? null : stillSelected,
        loading: false, error: '',
      };
    } catch (err) {
      if (seq !== this.loadSeq) return;
      this.data.bulletin = { ...before, rows: [], loading: false, error: err instanceof Error ? err.message : '불러오기 실패' };
    }
    this.rerenderBody();
  }

  /** fetchPendingParts — 구 PendingPartsView.load 그대로 */
  private async loadRequests(): Promise<void> {
    try {
      const rows = await api.fetchPendingParts();
      this.data.requests = { rows, loading: false, error: '' };
    } catch (err) {
      this.data.requests = { rows: [], loading: false, error: err instanceof Error ? err.message : '불러오기 실패' };
    }
    this.rerenderBody();
  }

  /** 서버가 돌려준 그 편으로 selected 와 목록의 같은 줄을 바꾼다 */
  private replaceSelected(doc: api.BulletinDocView): void {
    const rows = this.data.bulletin.rows.map((row) => (isSameDoc(row, doc) ? doc : row));
    this.data.bulletin = { ...this.data.bulletin, rows, selected: doc };
    this.rerenderBody();
  }

  private selectedKey(): api.BulletinDocKey {
    const selected = this.data.bulletin.selected;
    assert(selected !== null, '한 편의 버튼은 오른쪽에 편이 있을 때만 그려진다 — selected 가 null 이면 버튼이 없어야 했다');

    return { vendor: selected.vendor, doc_no: selected.doc_no };
  }

  private renderBody(): string {
    switch (this.data.section) {
      case 'bulletin':
        return `
          <div class="ce-layout">
            <aside class="ce-list">${renderBulletinList(this.data.bulletin)}</aside>
            <section class="ce-doc">${renderBulletinDoc(this.data.bulletin)}</section>
          </div>`;
      case 'requests':
        return renderRequests(this.data.requests);
      case 'edit':
        return `<div class="text-muted ce-note">② 카탈로그 수정 — 준비 중</div>`;
    }
  }

  private rerenderBody(): void {
    this.refreshRegion('.ce-body', () => this.renderBody());
  }
}
